// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

// Environment detection for isomorphic usage
const isServer = typeof window === 'undefined';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // If marketplaceRootURL is provided, use it
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }

  // Environment-specific fallbacks
  if (isServer) {
    // Server-side: use environment variable or localhost fallback
    return process.env.REACT_APP_MARKETPLACE_ROOT_URL?.replace(/\/$/, '') || 'http://localhost:3000';
  } else {
    // Client-side: use window.location.origin
    return window.location.origin;
  }
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// Get the appropriate fetch function for the current environment
const getFetch = () => {
  return isServer ? fetch : window.fetch;
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  const fetchFn = getFetch();

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Add a get method for making GET requests
const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Add a JSON-only get method for custom endpoints that return JSON
const getJSON = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    headers: {
      'Accept': 'application/json',
      ...options.headers
    }
  };

  const url = `${apiBaseUrl()}${path}`;
  const fetchFn = getFetch();
  
  return fetchFn(url, {
    credentials: 'include',
    ...requestOptions,
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json(); // Always parse as JSON
  });
};

// Export the get methods
export { get, getJSON };

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Create platform stripe payment intent
//
// This endpoint allows creation of payment intents for platform-handled payments
// See server/api/platform-stripe-payment-intent.js for details
export const createPlatformStripePaymentIntent = body => {
  return post('/api/platform-stripe-payment-intent', body);
};

// Capture platform stripe payment intent
//
// This endpoint captures a previously created payment intent for platform-handled payments
// See server/api/platform-stripe-capture-payment.js for details
export const capturePlatformStripePaymentIntent = body => {
  return post('/api/platform-stripe-capture-payment', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Validate promo code
export const validatePromoCode = (promoCode, listingId) => {
  return post('/api/validate-promo-code', { promoCode, listingId });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Update the likes count for a listing
//
// This endpoint updates the likes count (stored in metadata) for a listing
// when a user adds or removes it from their wishlist.
// The server uses the Integration SDK to update listings that don't belong to the current user.
//
// See `server/api/update-listing-likes.js` to see how the data is processed.
//
// @param {Object} body - Contains listingId and increment flag
// @param {String} body.listingId - ID of the listing to update
// @param {Boolean} body.increment - Whether to increment (true) or decrement (false) the count
// @returns {Promise<Object>} - Promise that resolves with the updated likes count
export const updateListingLikes = body => {
  return post('/api/update-listing-likes', body);
};

// Fetch all usernames from the server
//
// This endpoint returns a map of user IDs to usernames
// Optional query parameters:
// - includeEmail: Whether to include email addresses in the response (default: false)
// - includeProfile: Whether to include full profile data (default: false)
// - userType: Filter users by userType in publicData (default: 'artist')
// - forceRefresh: Whether to bypass the cache and fetch fresh data (default: false)
// - artistCategory: Filter users by metadata.artistCategory (string)
// - isFeatured: Filter users by metadata.isFeatured (boolean)
// - sort: Sort users by metadata field ('artistRating' for descending order)
export const getAllUsers = (options = {}) => {
  const { userType, forceRefresh, artistCategory, isFeatured, sort, isDiscoverable } = options;
  
  // Build query string
  const queryParams = new URLSearchParams();
  if (userType) queryParams.append('userType', userType);
  if (forceRefresh) queryParams.append('forceRefresh', 'true');
  if (artistCategory) queryParams.append('artistCategory', artistCategory);
  if (isFeatured !== undefined) queryParams.append('isFeatured', isFeatured.toString());
  if (isDiscoverable) queryParams.append('isDiscoverable', isDiscoverable.toString());
  if (sort) queryParams.append('sort', sort);
  
  const queryString = queryParams.toString();
  const path = queryString ? `/api/users?${queryString}` : '/api/users';
  
  return getJSON(path);
};

// Clear the usernames cache
//
// This endpoint clears the server-side cache for the usernames endpoint
export const clearUsernamesCache = () => {
  return get('/api/users/clear-usernames-cache');
};

// Upload banner image to Cloudflare Images
//
// This endpoint uploads a banner image file to Cloudflare Images and returns the image URL.
// The file should be sent as FormData.
// 
// @param {FormData} body - FormData containing the image file
// @returns {Promise<Object>} - Promise that resolves with the image URL
export const uploadBannerImage = body => {
  // File uploads are browser-only functionality
  if (isServer) {
    return Promise.reject(new Error('File uploads are not supported in server-side rendering'));
  }

  // For file uploads, we need to bypass the default JSON serialization
  // and let the browser handle the FormData and Content-Type
  const url = `${apiBaseUrl()}/api/upload-banner-image`;
  
  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    body, // Don't serialize FormData - pass it directly
    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
  };

  const fetchFn = getFetch();

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Upload video to Cloudflare Stream
//
// This endpoint uploads a video file to Cloudflare Stream and returns the video data.
// The file should be sent as FormData.
// 
// @param {FormData} body - FormData containing the video file
// @returns {Promise<Object>} - Promise that resolves with video data (videoId, thumbnailUrl, streamUrl, etc.)
export const uploadVideo = body => {
  // File uploads are browser-only functionality
  if (isServer) {
    return Promise.reject(new Error('File uploads are not supported in server-side rendering'));
  }

  // For file uploads, we need to bypass the default JSON serialization
  // and let the browser handle the FormData and Content-Type
  const url = `${apiBaseUrl()}/api/upload-video`;
  
  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    body, // Don't serialize FormData - pass it directly
    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
  };

  const fetchFn = getFetch();

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Upload room mockup image to Cloudflare Images
//
// This endpoint uploads a room image file to Cloudflare Images for visualizing artwork on walls.
// The file should be sent as FormData.
//
// @param {FormData} body - FormData containing the image file
// @returns {Promise<Object>} - Promise that resolves with the image URL and metadata
export const uploadRoomMockup = body => {
  // File uploads are browser-only functionality
  if (isServer) {
    return Promise.reject(new Error('File uploads are not supported in server-side rendering'));
  }

  // For file uploads, we need to bypass the default JSON serialization
  // and let the browser handle the FormData and Content-Type
  const url = `${apiBaseUrl()}/api/upload-room-mockup`;

  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    body, // Don't serialize FormData - pass it directly
    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
  };

  const fetchFn = getFetch();

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Upload message attachment (PDF, JPEG, or PNG, max 2MB)
//
// Images are stored in Cloudflare Images; PDFs are stored in Cloudflare R2.
// Returns { url, name, type } for embedding in the message content.
//
// @param {FormData} body - FormData containing a single "file" field
// @returns {Promise<{url: string, name: string, type: string}>}
export const uploadMessageAttachment = body => {
  if (isServer) {
    return Promise.reject(new Error('File uploads are not supported in server-side rendering'));
  }

  const url = `${apiBaseUrl()}/api/upload-message-attachment`;

  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    body,
  };

  const fetchFn = getFetch();

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Poll mockup status
//
// Poll the server for mockup processing job status.
// Polls every 5 seconds until job is completed or failed.
//
// @param {string} jobId - Unique job identifier
// @param {function} onProgress - Optional callback for progress updates (receives status object)
// @returns {Promise<Object>} - Promise that resolves with final result or rejects on error
export const pollMockupStatus = (jobId, onProgress = null) => {
  // Browser-only functionality
  if (isServer) {
    return Promise.reject(new Error('Polling is not supported in server-side rendering'));
  }

  const POLL_INTERVAL_DEFAULT = 5000; // 5 seconds for most statuses
  const POLL_INTERVAL_AI = 1500; // 1.5 seconds during AI processing to catch partials
  const url = `${apiBaseUrl()}/api/mockup-status/${jobId}`;
  const fetchFn = getFetch();

  let isCancelled = false;
  let timeoutId = null;
  let currentStatus = null;

  const poll = () => {
    if (isCancelled) {
      return Promise.reject(new Error('Polling cancelled'));
    }

    return fetchFn(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        // Job not found on server (expired or never existed)
        if (res.status === 404) {
          return { exists: false, notFound: true };
        }

        if (res.status >= 400) {
          return res.json().then(data => {
            let e = new Error(data.error || 'Failed to check status');
            e = Object.assign(e, data);
            throw e;
          });
        }

        return res.json();
      })
      .then(statusData => {
        // Job not found - resolve with notFound flag (silent cleanup)
        if (statusData.notFound || !statusData.exists) {
          return { notFound: true };
        }

        // Call progress callback if provided
        if (onProgress && typeof onProgress === 'function') {
          onProgress(statusData);
        }

        // Check if job is complete
        if (statusData.status === 'completed') {
          return statusData.data;
        }

        // Check if job failed
        if (statusData.status === 'failed') {
          const error = new Error(statusData.error || 'Processing failed');
          error.statusData = statusData;
          throw error;
        }

        // Track current status for adaptive polling
        currentStatus = statusData.status;

        // Job still processing - continue polling
        // Use shorter interval during AI processing to catch partial images
        const pollInterval = currentStatus === 'ai-processing'
          ? POLL_INTERVAL_AI
          : POLL_INTERVAL_DEFAULT;

        return new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => {
            poll()
              .then(resolve)
              .catch(reject);
          }, pollInterval);
        });
      });
  };

  // Start polling
  const pollingPromise = poll();

  // Add cancel method to promise
  pollingPromise.cancel = () => {
    isCancelled = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return pollingPromise;
};

// Get the full URL for a mockup partial image
// The server returns relative URLs like /api/mockup-partial-image/...
// This converts them to full URLs with the correct API base
export const getMockupPartialImageUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  // If already a full URL, return as-is
  if (relativeUrl.startsWith('http')) return relativeUrl;
  // If it's a data URL, return as-is
  if (relativeUrl.startsWith('data:')) return relativeUrl;
  // Prepend the API base URL
  return `${apiBaseUrl()}${relativeUrl}`;
};

// Get all artists with their listing images
//
// This endpoint returns artists data along with their first 4 listing images.
// The server uses caching to optimize performance.
//
// @returns {Promise<Object>} - Promise that resolves with artists and their listing images
export const getArtistsWithListings = () => {
  return get('/api/artists/with-listings');
};

// LLM-based spam/phishing classifier for outgoing messages.
// `transactionId` is optional but recommended — it's forwarded to admin
// notifications so the team can jump straight to the offending thread.
// Returns { isSpam, confidence, category, reason, checked }.
// Fails open on the server, so callers should treat any error as "not spam".
export const checkMessageSpam = (message, transactionId) => {
  const url = `${apiBaseUrl()}/api/check-message-spam`;
  const fetchFn = getFetch();
  return fetchFn(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ message, transactionId: transactionId || null }),
  }).then(res => {
    if (res.status >= 400) {
      return {
        isSpam: false,
        confidence: 0,
        category: 'not_spam',
        reason: 'http_error',
        checked: false,
      };
    }
    return res.json();
  });
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};
